/**
 * ContractEditor Component
 * 
 * Rich text editor using Lexical for editing employment contracts.
 * Supports locking during AI operations to prevent concurrent edits.
 */

import { useEffect, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { $getRoot, $insertNodes, $getSelection, $isRangeSelection } from 'lexical';
import { Loader2 } from 'lucide-react';

interface ContractEditorProps {
  initialHtml: string;
  onContentChange: (html: string) => void;
  isLocked: boolean;
}

/**
 * Plugin to handle content initialization and change tracking
 */
function ContentChangePlugin({ 
  initialHtml, 
  onContentChange 
}: { 
  initialHtml: string; 
  onContentChange: (html: string) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const isInitialMount = useRef(true);
  const lastAppliedHtml = useRef<string>('');

  // Initialize editor with HTML content
  useEffect(() => {
    if (initialHtml && initialHtml !== lastAppliedHtml.current) {
      editor.update(() => {
        // Get current HTML to compare
        const currentHtml = $generateHtmlFromNodes(editor);
        
        // Only update if the HTML is actually different (avoid update loop)
        if (currentHtml !== initialHtml) {
          // Store current selection before clearing
          const selection = $getSelection();
          let savedOffset = 0;
          let savedKey = null;
          
          if (selection && $isRangeSelection(selection) && !isInitialMount.current) {
            const anchor = selection.anchor;
            savedOffset = anchor.offset;
            savedKey = anchor.key;
          }
          
          // Parse and insert new content
          const parser = new DOMParser();
          const dom = parser.parseFromString(initialHtml, 'text/html');
          const nodes = $generateNodesFromDOM(editor, dom);
          
          $getRoot().clear();
          $insertNodes(nodes);
          
          // Try to restore cursor position after AI edit
          if (savedKey && !isInitialMount.current) {
            try {
              const root = $getRoot();
              // Try to find the node with the same key, or just move to start
              const firstChild = root.getFirstChild();
              if (firstChild) {
                firstChild.selectStart();
              }
            } catch (e) {
              // If restoration fails, just leave cursor where it lands
              console.debug('Could not restore cursor position:', e);
            }
          }
          
          lastAppliedHtml.current = initialHtml;
          isInitialMount.current = false;
        }
      });
    }
  }, [initialHtml, editor]);

  // Track content changes
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const htmlString = $generateHtmlFromNodes(editor);
        onContentChange(htmlString);
      });
    });
  }, [editor, onContentChange]);

  return null;
}

export const ContractEditor: React.FC<ContractEditorProps> = ({
  initialHtml,
  onContentChange,
  isLocked,
}) => {
  const initialConfig = {
    namespace: 'ContractEditor',
    theme: {
      paragraph: 'mb-2',
      heading: {
        h1: 'text-2xl font-bold mb-4',
        h2: 'text-xl font-bold mb-3',
        h3: 'text-lg font-bold mb-2',
      },
      text: {
        bold: 'font-bold',
        italic: 'italic',
        underline: 'underline',
      },
    },
    onError: (error: Error) => {
      console.error('Lexical error:', error);
    },
    editable: !isLocked,
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="relative h-full">
        {isLocked && (
          <div className="absolute inset-0 bg-gray-100/50 z-10 flex items-center justify-center">
            <div className="bg-white p-4 rounded-lg shadow-lg flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
              <span className="text-gray-700">AI is making changes...</span>
            </div>
          </div>
        )}
        <div className="h-full overflow-auto bg-white">
          <RichTextPlugin
            contentEditable={
              <ContentEditable 
                className="min-h-full p-8 focus:outline-none prose prose-slate max-w-none" 
              />
            }
            placeholder={
              <div className="absolute top-8 left-8 text-gray-400 pointer-events-none">
                Start editing your contract...
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ContentChangePlugin
            initialHtml={initialHtml}
            onContentChange={onContentChange}
          />
        </div>
      </div>
    </LexicalComposer>
  );
};

